import {FormEvent, useEffect, useState} from "react";
import {Alert, Button, Card, Form, InputGroup} from "react-bootstrap";
import {useNavigate} from "react-router";
import {ProductsApi} from "../../../api/ProductsApi";
import {Editions, ProductViewModel} from "../../../api/models";
import {ErrorAlert, fieldError} from "../../../components/ErrorAlert";
import {SubmitButton} from "../../../components/SubmitButton";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {fromMinorUnits, toMinorUnits} from "../../../components/format";
import {RichTextEditor} from "../../../components/RichTextEditor";
import {Field, FieldControl} from "../../../components/Field";
import {BRAND_ACCENT} from "../../../components/accent";

// A price as typed: the amount stays text until saving.
interface PriceRow {
    edition: string;
    amount: string;
    currency: string;
}

const toRows = (product: ProductViewModel): PriceRow[] =>
    product.prices.map(x => ({ edition: x.edition, amount: fromMinorUnits(x.amount, x.currency), currency: x.currency }));

export function ProductForm({product, onSaved}: { product: ProductViewModel, onSaved(product: ProductViewModel): void }) {
    const navigate = useNavigate();
    const [name, setName] = useState(product.name);
    const [sku, setSku] = useState(product.sku);
    const [keyPrefix, setKeyPrefix] = useState(product.keyPrefix);
    const [description, setDescription] = useState(product.description);
    const [published, setPublished] = useState(product.published);
    const [beta, setBeta] = useState(product.beta);
    const [accentColor, setAccentColor] = useState(product.accentColor ?? '');
    const [prices, setPrices] = useState<PriceRow[]>(toRows(product));
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        setName(product.name);
        setSku(product.sku);
        setKeyPrefix(product.keyPrefix);
        setDescription(product.description);
        setPublished(product.published);
        setBeta(product.beta);
        setAccentColor(product.accentColor ?? '');
        setPrices(toRows(product));
    }, [product]);

    const prefixValid = /^[A-Za-z0-9]{2,8}$/.test(keyPrefix.trim());
    const priceProblem = (row: PriceRow) => !row.edition.trim() || !/^[A-Za-z]{3}$/.test(row.currency.trim()) || toMinorUnits(row.amount, row.currency.trim().toUpperCase()) === undefined;
    const setPrice = (index: number, changes: Partial<PriceRow>) => setPrices(rows => rows.map((x, i) => i === index ? { ...x, ...changes } : x));

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!name.trim() || !sku.trim() || !prefixValid || prices.some(priceProblem)) {
            return;
        }
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            onSaved(await ProductsApi.update(product.id, {
                name: name.trim(),
                sku: sku.trim(),
                keyPrefix: keyPrefix.trim(),
                description: description.trim(),
                published,
                beta,
                accentColor,
                prices: prices.map(x => {
                    const currency = x.currency.trim().toUpperCase();
                    return { edition: x.edition.trim(), currency, amount: toMinorUnits(x.amount, currency)! };
                })
            }));
            setSaved(true);
            setValidated(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        setError(undefined);
        try {
            await ProductsApi.remove(product.id);
            navigate('/admin/products');
        } catch (err) {
            setError(err);
            setBusy(false);
            setConfirmDelete(false);
        }
    };

    return (
        <Card>
            <Card.Body>
                <ErrorAlert error={error} shownFields={['name', 'sku', 'keyPrefix', 'accentColor']}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                <Form noValidate onSubmit={submit}>
                    <Field className="mb-3" controlId="product-name" label="Name" error={(validated && !name.trim() && 'Enter a name.') || fieldError(error, 'name')}>
                        <FieldControl value={name} onChange={e => setName(e.target.value)}/>
                    </Field>
                    <div className="d-flex gap-3">
                        <Field className="mb-3 flex-grow-1" controlId="product-sku" label="SKU" error={(validated && !sku.trim() && 'Enter a SKU.') || fieldError(error, 'sku')}>
                            <FieldControl className="license-key" value={sku} onChange={e => setSku(e.target.value)} aria-describedby="product-sku-warning"/>
                        </Field>
                        <Field className="mb-3 flex-grow-1" controlId="product-prefix" label="License key prefix" error={(validated && !prefixValid && '2 to 8 letters or digits.') || fieldError(error, 'keyPrefix')}>
                            <FieldControl className="license-key" value={keyPrefix} onChange={e => setKeyPrefix(e.target.value)}/>
                        </Field>
                    </div>
                    <Form.Text as="p" id="product-sku-warning" className="mt-n2">
                        Changing the SKU breaks activation and update checks for builds that send the old one.
                    </Form.Text>
                    <div className="mb-3">
                        <Form.Label id="product-description-label">Description</Form.Label>
                        <RichTextEditor labelledBy="product-description-label" value={description} onChange={setDescription}/>
                    </div>

                    {/* The browser's color picker; empty is the site's own color. */}
                    <Field className="mb-3" controlId="product-accent" label="Accent color" error={fieldError(error, 'accentColor')}
                           help="The color of this product's page and its spot on the home page: usually its user interface's.">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <FieldControl type="color" className="form-control-color" value={accentColor || BRAND_ACCENT}
                                          onChange={e => setAccentColor(e.target.value)}/>
                            <span className="license-key">{accentColor || "The site's color"}</span>
                            {accentColor && <Button size="sm" variant="outline-secondary" onClick={() => setAccentColor('')}>Use the site's color</Button>}
                        </div>
                    </Field>

                    <Form.Label>Prices</Form.Label>
                    {prices.map((row, index) => {
                        const editionBad = validated && !row.edition.trim();
                        const amountBad = validated && toMinorUnits(row.amount, row.currency.trim().toUpperCase()) === undefined;
                        const currencyBad = validated && !/^[A-Za-z]{3}$/.test(row.currency.trim());
                        const errorId = `product-price-${index}-error`;
                        const invalid = (bad: boolean) => ({ isInvalid: bad, 'aria-invalid': bad || undefined, 'aria-describedby': bad ? errorId : undefined });
                        return (
                            <div key={index} className="mb-2">
                                <InputGroup hasValidation>
                                    <Form.Control list="product-editions" placeholder="Edition" aria-label={`Price ${index + 1} edition`} value={row.edition}
                                                  onChange={e => setPrice(index, { edition: e.target.value })} {...invalid(editionBad)}/>
                                    <Form.Control placeholder="49.00" aria-label={`Price ${index + 1} amount`} inputMode="decimal" value={row.amount}
                                                  onChange={e => setPrice(index, { amount: e.target.value })} {...invalid(amountBad)}/>
                                    <Form.Control placeholder="USD" aria-label={`Price ${index + 1} currency`} style={{ maxWidth: '6rem' }} value={row.currency}
                                                  onChange={e => setPrice(index, { currency: e.target.value })} {...invalid(currencyBad)}/>
                                    <Button variant="outline-danger" aria-label={`Remove price ${index + 1}`} onClick={() => setPrices(rows => rows.filter((_, i) => i !== index))}>
                                        <i className="bi bi-x-lg" aria-hidden="true"></i>
                                    </Button>
                                </InputGroup>
                                {(editionBad || amountBad || currencyBad) && (
                                    <div id={errorId} className="invalid-feedback d-block">
                                        Each price needs an edition, an amount such as 49.00, and a three-letter currency code such as USD.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <datalist id="product-editions">{Editions.map(x => <option key={x} value={x}/>)}</datalist>
                    <Button size="sm" variant="outline-secondary" className="mb-3"
                            onClick={() => setPrices(rows => [...rows, { edition: '', amount: '', currency: rows[rows.length - 1]?.currency ?? 'USD' }])}>
                        <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>Add price
                    </Button>

                    <Form.Check type="switch" id="product-published" className="mb-2" label="Published: listed on the website"
                                checked={published} onChange={e => setPublished(e.target.checked)}/>
                    <Form.Check type="switch" id="product-beta" className="mb-4" label="Early Access: invited Early Access Users only, not on sale yet"
                                checked={beta} onChange={e => setBeta(e.target.checked)}/>

                    <div className="d-flex flex-wrap gap-2">
                        <SubmitButton busy={busy}>Save</SubmitButton>
                        <Button variant="outline-danger" className="ms-auto" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete…</Button>
                    </div>
                </Form>
            </Card.Body>
            <ConfirmModal show={confirmDelete} title="Delete this product?" confirmLabel="Delete" busy={busy}
                          onConfirm={remove} onCancel={() => setConfirmDelete(false)}>
                <p>Its versions, their files and its images are deleted too. This can't be undone.</p>
                <p className="mb-0 text-body-secondary">A product with licenses can't be deleted: unpublish it instead.</p>
            </ConfirmModal>
        </Card>
    );
}
